import { List, Map, Set } from "immutable";
import { createSelector } from "reselect";

import { TUTORIAL_PANEL } from "../constants/identifiers";
import { activeTutorialPathSelectors } from "../modules/activeTutorialPath";
import { appStateSelectors } from "../modules/appState";
import { tutorialSelectors } from "../modules/tutorial";
import { createFromJS, immutableRecords } from "../types";
import preprocessorQuery from "../utils/preprocessorQuery";
import {
  tutorialAccessGatesById,
  tutorialApplicableGatesById,
  tutorialDismissalGatesById,
  tutorialGroups,
  tutorialLinksById
} from "./index";
import { replaceTutorialPlaceholders } from "./utils";

const isMobile = preprocessorQuery.isMobile();

const createTutorialDisplayGroup = (group, completedSet) => {
  // Get updated tips with their complete status
  const tips = List(group.tips).map(t =>
    createFromJS.TutorialTipDisplayRecord(t).set("isComplete", completedSet.has(t.id))
  );
  // Are all tips complete, or is the group itself
  // marked as complete?
  const isComplete = completedSet.has(group.id) || !tips.find(t => !t.isComplete);
  const data = Object.assign({}, group, { tips, isComplete });
  return immutableRecords.TutorialGroupDisplayRecord(data);
};

const createTutorialDisplayGroups = (groups, completedSet, tutorialPath) => {
  const allIds = groups.allIdsByPath[tutorialPath];
  return immutableRecords.IdReferenceList({
    byId: Map(groups.byId)
      .filter(g => allIds.indexOf(g.id) >= 0)
      .map(g => createTutorialDisplayGroup(g, completedSet)),
    allIds: List(allIds)
  });
};

const getTutorialDisplayStatus = createSelector(
  // input
  () => tutorialGroups,
  activeTutorialPathSelectors.getActiveTutorialPath,
  tutorialSelectors.getCompletedTips,
  tutorialSelectors.getActiveTutorial,
  tutorialSelectors.getVisibleTutorial,
  tutorialSelectors.getTutorialSilenced,
  // output
  (groups, tutorialPath, completedSet, activeTutorial, visibleTutorial, tutorialSilenced) => {
    const displayGroups = tutorialPath
      ? createTutorialDisplayGroups(groups, completedSet, tutorialPath)
      : immutableRecords.IdReferenceList({
        byId: Map(),
        allIds: List()
      });
    const hasIncompleteGroup = tutorialPath ? Boolean(displayGroups.byId.find(g => !g.isComplete)) : true;
    return immutableRecords.TutorialDisplayRecord({
      groups: displayGroups,
      activeTutorial,
      visibleTutorial,
      tutorialIsActive: hasIncompleteGroup && !tutorialSilenced
    });
  }
);

const getTutorialDisplayStatusActiveGroup = state => {
  const d = getTutorialDisplayStatus(state);
  return d.groups.byId.get(d.activeTutorial);
};

const getTutorialDisplayStatusForGroup = (state, groupId) => {
  const d = getTutorialDisplayStatus(state);
  return d.groups.byId.get(groupId);
};

const getTutorialDisplayStatusActiveTip = state => {
  const g = getTutorialDisplayStatusActiveGroup(state);
  if (!g) {
    return null;
  }
  const tip = g.tips.find(t => !t.isComplete);
  return tip;
};

const getTutorialActiveTipIsDismissable = state => {
  const activeTip = getTutorialDisplayStatusActiveTip(state);
  if (activeTip && activeTip.requiresDismissal) {
    const dismissalGate = tutorialDismissalGatesById[activeTip.id];
    return !dismissalGate || dismissalGate(state);
  }
  return false;
};

/**
 * @param(*) groups
 * @param(List) sidebarTypes
 * @param(List) orderedNarrativeTypes
 * @param(Map) typesById
 * @param(string) defaultSequenceType
 * @param(List) narrativeSectionTypes
 * @param(string) syncAppName
 */
const getTutorialDropdownOptionsFromGroups = createSelector(
  // fetch parameters for last calculate function
  (...params) => params[0],
  (...params) => params[1],
  (...params) => params[2],
  (...params) => params[3],
  (...params) => params[4],
  (...params) => params[5],
  (...params) => params[6],
  // Calculate result
  (groups, sidebarTypes, orderedNarrativeTypes, typesById, defaultSequenceType, narrativeSectionTypes, syncAppName) => {
    return groups.allIds.map(id => {
      const group = groups.byId.get(id);
      return immutableRecords.DropdownOptionRecord({
        label: group.label,
        value: group.id,
        description: replaceTutorialPlaceholders(
          group.description,
          sidebarTypes,
          orderedNarrativeTypes,
          typesById,
          defaultSequenceType,
          narrativeSectionTypes,
          syncAppName
        ),
        icon: group.isComplete ? "statusValid" : "statusOpen",
        color: group.isComplete ? "statusValid" : "iconMid"
      });
    });
  }
);

const getAccessRestrictedTipsInActiveGroup = state => {
  const group = getTutorialDisplayStatusActiveGroup(state);
  if (!group) {
    return Set();
  }
  return Set(
    group.tips
      .filter(t => {
        if (tutorialAccessGatesById[t.id]) {
          return !tutorialAccessGatesById[t.id](state);
        }
        return false;
      })
      .map(t => t.id)
  );
};

const getAccessRestrictedTipsInGroup = (state, groupId) => {
  const group = getTutorialDisplayStatusForGroup(state, groupId);
  if (!group) {
    return Set();
  }
  return Set(
    group.tips
      .filter(t => {
        if (tutorialAccessGatesById[t.id]) {
          return !tutorialAccessGatesById[t.id](state);
        }
        return false;
      })
      .map(t => t.id)
  );
};

const isActiveGateAccessRestricted = state => {
  const group = getTutorialDisplayStatusActiveGroup(state);
  if (group) {
    if (tutorialAccessGatesById[group.id]) {
      return !tutorialAccessGatesById[group.id](state);
    }
  }
  return false;
};

const isGroupGateAccessRestricted = (state, groupId) => {
  if (tutorialAccessGatesById[groupId]) {
    return !tutorialAccessGatesById[groupId](state);
  }
  return false;
};

const isGroupGateApplicable = (state, groupId) => {
  if (tutorialApplicableGatesById[groupId]) {
    return !tutorialApplicableGatesById[groupId](state);
  }
  return false;
};

const getGroupLinks = groupId => {
  if (tutorialLinksById[groupId]) {
    return tutorialLinksById[groupId];
  }
  return false;
};

const tutorialIsActive = state => {
  return getTutorialDisplayStatus(state).tutorialIsActive;
};

const getShowTutorialNotification = createSelector(
  // inputs
  tutorialSelectors.getTutorialSilenced,
  getTutorialDisplayStatusActiveTip,
  isActiveGateAccessRestricted,
  appStateSelectors.getLeftPanelMode,
  appStateSelectors.getRightPanelMode,
  // calculate based on inputs
  (tutorialSilenced, activeTip, isGroupBlocked, leftPanelMode, rightPanelMode) => {
    if (
      !activeTip ||
      isGroupBlocked ||
      tutorialSilenced ||
      rightPanelMode === TUTORIAL_PANEL ||
      leftPanelMode === TUTORIAL_PANEL ||
      isMobile
    ) {
      return false;
    }
    return true;
  }
);

export const tutorialDisplaySelectors = {
  getTutorialDisplayStatus,
  getTutorialDropdownOptionsFromGroups,
  getAccessRestrictedTipsInActiveGroup,
  getAccessRestrictedTipsInGroup,
  getTutorialDisplayStatusActiveGroup,
  getTutorialDisplayStatusForGroup,
  getTutorialDisplayStatusActiveTip,
  getTutorialActiveTipIsDismissable,
  isActiveGateAccessRestricted,
  isGroupGateAccessRestricted,
  isGroupGateApplicable,
  getGroupLinks,
  tutorialIsActive,
  getShowTutorialNotification
};
